/**
 * Typed API client for Peekbox backend.
 * Reads JWT from Supabase session for auth.
 *
 * All functions throw ApiClientError on non-2xx responses.
 */

import { supabase } from '../auth/supabase';
import type {
  Box,
  BoxWithItems,
  Item,
  PaginatedResult,
  MarkerRef,
  ApiError,
  MarkerType,
} from '@shared/types';

const API_BASE = '/api/v1';

// ── Error type ────────────────────────────────────────────────

export class ApiClientError extends Error {
  constructor(
    public statusCode: number,
    public errorType: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// ── Base fetch ────────────────────────────────────────────────

async function getAuthToken(): Promise<string | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token ?? null;
}

export async function apiFetch<T>(
  path: string,
  // eslint-disable-next-line no-undef
  options: RequestInit = {},
): Promise<T> {
  const token = await getAuthToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let body: ApiError;
    try {
      body = (await response.json()) as ApiError;
    } catch {
      body = {
        statusCode: response.status,
        error: response.statusText,
        message: 'An unexpected error occurred',
      };
    }
    throw new ApiClientError(body.statusCode, body.error, body.message);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

// ── Query helper ──────────────────────────────────────────────

export interface ListParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function toQueryString(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}

// ── Box API ───────────────────────────────────────────────────

export async function listBoxes(params?: ListParams): Promise<PaginatedResult<Box>> {
  const qs = toQueryString({ ...params });
  return apiFetch<PaginatedResult<Box>>(`/boxes${qs}`);
}

export async function searchBoxes(
  q: string,
  params?: ListParams,
): Promise<PaginatedResult<BoxWithItems>> {
  const qs = toQueryString({ q, ...params });
  return apiFetch<PaginatedResult<BoxWithItems>>(`/search${qs}`);
}

export async function getBox(id: string): Promise<BoxWithItems> {
  return apiFetch<BoxWithItems>(`/boxes/${encodeURIComponent(id)}`);
}

export interface CreateBoxBody {
  markerType: MarkerType;
  markerId: string;
  name: string;
  location?: string;
  groupId?: string;
  notes?: string;
}

export async function createBox(data: CreateBoxBody): Promise<Box> {
  return apiFetch<Box>('/boxes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface UpdateBoxBody {
  name?: string;
  markerType?: MarkerType;
  markerId?: string;
  location?: string | null;
  locationId?: string | null;
  groupId?: string | null;
  notes?: string | null;
}

export async function updateBox(id: string, data: UpdateBoxBody): Promise<Box> {
  return apiFetch<Box>(`/boxes/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBox(id: string): Promise<void> {
  return apiFetch<void>(`/boxes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

export async function lookupMarker(type: MarkerType, markerId: string): Promise<BoxWithItems> {
  return apiFetch<BoxWithItems>(
    `/boxes/marker/${encodeURIComponent(type)}/${encodeURIComponent(markerId)}`,
  );
}

export async function batchLookup(markers: MarkerRef[]): Promise<BoxWithItems[]> {
  return apiFetch<BoxWithItems[]>('/boxes/batch-lookup', {
    method: 'POST',
    body: JSON.stringify({ markers }),
  });
}

// ── Item API ──────────────────────────────────────────────────

export interface CreateItemBody {
  name: string;
  quantity?: number;
}

export async function addItem(boxId: string, data: CreateItemBody): Promise<Item> {
  return apiFetch<Item>(`/boxes/${encodeURIComponent(boxId)}/items`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export interface UpdateItemBody {
  name?: string;
  quantity?: number;
}

export async function updateItem(id: string, data: UpdateItemBody): Promise<Item> {
  return apiFetch<Item>(`/items/${encodeURIComponent(id)}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteItem(id: string): Promise<void> {
  return apiFetch<void>(`/items/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
}

/**
 * Adjust item quantity by delta (+/- increment).
 */
export async function adjustItemQuantity(id: string, delta: number): Promise<Item> {
  return apiFetch<Item>(`/items/${encodeURIComponent(id)}/quantity`, {
    method: 'PATCH',
    body: JSON.stringify({ delta }),
  });
}

// ── Photo API ────────────────────────────────────────────────

export interface PhotoUploadResult {
  url: string;
  key: string;
}

/**
 * Upload a photo for a box.
 */
export async function uploadBoxPhoto(boxId: string, file: File): Promise<PhotoUploadResult> {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append('file', file);
  formData.append('targetType', 'box');
  formData.append('targetId', boxId);

  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_BASE}/photos/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });

  if (!response.ok) {
    let body: ApiError;
    try {
      body = (await response.json()) as ApiError;
    } catch {
      body = { statusCode: response.status, error: response.statusText, message: 'Upload failed' };
    }
    throw new ApiClientError(body.statusCode, body.error, body.message);
  }

  return response.json() as Promise<PhotoUploadResult>;
}

/**
 * Delete a box's photo (sets photoUrl to null).
 */
export async function deleteBoxPhoto(boxId: string): Promise<void> {
  // Update the box to clear photoUrl
  await apiFetch(`/boxes/${encodeURIComponent(boxId)}`, {
    method: 'PUT',
    body: JSON.stringify({ photoUrl: null }),
  });
}
